import API, { License, LicenseCreateRequest, LicenseStatistics, LicenseUpdateRequest } from "@/api/api";
import { toast } from "@/lib/instrumented-toast";
import { queryKeys } from "@/lib/query-keys";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

const api = new API();

// Fetch licenses
export function useLicenses(orgId?: string, includeInactive = false) {
  return useQuery({
    queryKey: queryKeys.licenses(orgId, includeInactive),
    queryFn: async (): Promise<License[]> => {
      const response = await api.listLicenses({
        org_id: orgId,
        include_inactive: includeInactive,
        page: 1,
        page_size: 100,
      });
      return response.licenses;
    },
  });
}

// Fetch license statistics
export function useLicenseStatistics(orgId?: string) {
  return useQuery({
    queryKey: queryKeys.licenseStatistics(orgId),
    queryFn: async (): Promise<LicenseStatistics> => {
      return await api.getLicenseStatistics(orgId);
    },
  });
}

// Generate license mutation
export function useGenerateLicense() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: LicenseCreateRequest): Promise<License> => {
      return await api.generateLicense(request);
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["licenses"] });
      if (variables.organization_id) {
        queryClient.invalidateQueries({ queryKey: queryKeys.licenseStatistics(variables.organization_id) });
      }
      toast.success("License generated successfully");
    },
    onError: (error) => {
      const errorMessage = error instanceof Error ? error.message : "Failed to generate license";
      toast.error(errorMessage);
    },
  });
}

// Update license mutation
export function useUpdateLicense() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, request }: { id: number; request: LicenseUpdateRequest }): Promise<License> => {
      return await api.updateLicense(id, request);
    },
    onSuccess: (updatedLicense) => {
      queryClient.invalidateQueries({ queryKey: ["licenses"] });
      if (updatedLicense.organization_id) {
        queryClient.invalidateQueries({ queryKey: queryKeys.licenseStatistics(updatedLicense.organization_id) });
      }
      toast.success("License updated successfully");
    },
    onError: (error) => {
      const errorMessage = error instanceof Error ? error.message : "Failed to update license";
      toast.error(errorMessage);
    },
  });
}

// Revoke license (set is_active = false)
export function useRevokeLicense() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: number): Promise<License> => {
      return await api.updateLicense(id, { is_active: false });
    },
    onSuccess: (updatedLicense) => {
      queryClient.invalidateQueries({ queryKey: ["licenses"] });
      if (updatedLicense.organization_id) {
        queryClient.invalidateQueries({ queryKey: queryKeys.licenseStatistics(updatedLicense.organization_id) });
      }
      toast.success("License revoked successfully");
    },
    onError: (error) => {
      const errorMessage = error instanceof Error ? error.message : "Failed to revoke license";
      toast.error(errorMessage);
    },
  });
}

// Reactivate license (set is_active = true)
export function useReactivateLicense() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: number): Promise<License> => {
      return await api.updateLicense(id, { is_active: true });
    },
    onSuccess: (updatedLicense) => {
      queryClient.invalidateQueries({ queryKey: ["licenses"] });
      if (updatedLicense.organization_id) {
        queryClient.invalidateQueries({ queryKey: queryKeys.licenseStatistics(updatedLicense.organization_id) });
      }
      toast.success("License reactivated successfully");
    },
    onError: (error) => {
      const errorMessage = error instanceof Error ? error.message : "Failed to reactivate license";
      toast.error(errorMessage);
    },
  });
}

// Validate license
export function useValidateLicense() {
  return useMutation({
    mutationFn: async ({ key, holder }: { key: string; holder: string }): Promise<boolean> => {
      const result = await api.validateLicense(key, holder);
      return result.is_valid && result.is_active;
    },
    onError: (error) => {
      const errorMessage = error instanceof Error ? error.message : "Failed to validate license";
      toast.error(errorMessage);
    },
  });
}
